import React, { useState, useRef } from 'react';
import { Mic, Square, Loader2, Sparkles } from 'lucide-react';
import { useLang } from '../../context/LanguageContext';
import { API_BASE as API } from '../../lib/apiBase';

export default function VocalRecorder({ sessionId, token, onVocalReady, onInspireResult }) {
  const { lang } = useLang();
  const [mode, setMode] = useState(null); // null | 'record' | 'inspire'
  const [recording, setRecording] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [timer, setTimer] = useState(0);
  const mediaRecRef = useRef(null);
  const chunksRef = useRef([]);
  const timerRef = useRef(null);
  const es = lang === 'es';

  const startRecording = async (recMode) => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const mr = new MediaRecorder(stream, { mimeType: 'audio/webm' });
      chunksRef.current = [];
      mr.ondataavailable = e => chunksRef.current.push(e.data);
      mr.onstop = () => {
        stream.getTracks().forEach(t => t.stop());
        const blob = new Blob(chunksRef.current, { type: 'audio/webm' });
        if (blob.size < 500) return;
        if (recMode === 'record') uploadVocal(blob);
        else if (recMode === 'inspire') uploadInspire(blob);
      };
      mr.start();
      mediaRecRef.current = mr;
      setMode(recMode);
      setRecording(true);
      setTimer(0);
      timerRef.current = setInterval(() => setTimer(p => p + 1), 1000);
    } catch (e) {
      console.error('Mic access denied:', e);
    }
  };

  const stopRecording = () => {
    if (mediaRecRef.current && mediaRecRef.current.state !== 'inactive') {
      mediaRecRef.current.stop();
    }
    setRecording(false);
    if (timerRef.current) {
      clearInterval(timerRef.current);
      timerRef.current = null;
    }
  };

  const uploadVocal = async (blob) => {
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append('audio', blob, 'vocal.webm');
      fd.append('session_id', sessionId || '');
      const r = await fetch(`${API}/api/producer/upload-vocal`, {
        method: 'POST', headers: { Authorization: `Bearer ${token}` }, body: fd,
      });
      const data = await r.json();
      if (data.ok && data.audio_url) {
        onVocalReady?.(data.audio_url);
      }
    } catch (e) {
      console.error('Upload vocal error:', e);
    } finally {
      setUploading(false);
      setMode(null);
    }
  };

  const uploadInspire = async (blob) => {
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append('audio', blob, 'reference.webm');
      fd.append('session_id', sessionId || '');
      const r = await fetch(`${API}/api/producer/inspire`, {
        method: 'POST', headers: { Authorization: `Bearer ${token}` }, body: fd,
      });
      const data = await r.json();
      if (data.ok) {
        onInspireResult?.(data);
      }
    } catch (e) {
      console.error('Inspire error:', e);
    } finally {
      setUploading(false);
      setMode(null);
    }
  };

  const formatTime = (s) => `${Math.floor(s / 60)}:${String(s % 60).padStart(2, '0')}`;

  if (recording) {
    return (
      <div data-testid="vocal-recorder-active" className="flex items-center gap-2 px-3 py-2 rounded-xl border border-red-500/30 bg-red-500/5">
        <div className="w-2 h-2 rounded-full bg-red-500 animate-pulse" />
        <span className="text-[10px] font-mono text-red-400">{formatTime(timer)}</span>
        <span className="text-[10px] text-[#A1A1AA]">
          {mode === 'record' ? (es ? 'Grabando voz...' : 'Recording voice...') : (es ? 'Capturando referencia...' : 'Capturing reference...')}
        </span>
        <button
          onClick={stopRecording}
          data-testid="vocal-stop-btn"
          className="ml-auto w-6 h-6 rounded-full bg-red-500 flex items-center justify-center hover:bg-red-400 transition-colors"
        >
          <Square size={10} className="text-white" />
        </button>
      </div>
    );
  }

  if (uploading) {
    return (
      <div data-testid="vocal-uploading" className="flex items-center gap-2 px-3 py-2 rounded-xl border border-[#F5C84230] bg-[#F5C84208]">
        <Loader2 size={12} className="text-[#F5C842] animate-spin" />
        <span className="text-[10px] text-[#A1A1AA]">
          {mode === 'record' ? (es ? 'Subiendo vocal...' : 'Uploading vocal...') : (es ? 'Analizando referencia...' : 'Analyzing reference...')}
        </span>
      </div>
    );
  }

  return (
    <div data-testid="vocal-recorder" className="flex items-center gap-1.5">
      <button
        onClick={() => startRecording('record')}
        data-testid="record-vocal-btn"
        className="flex items-center gap-1 px-2 py-1.5 rounded-lg border border-[#00D4FF30] text-[9px] text-[#00D4FF] hover:bg-[#00D4FF10] transition-all"
        title={es ? 'Graba tu voz en la pista' : 'Record your voice on the track'}
      >
        <Mic size={10} />
        <span>{es ? 'Grabar voz' : 'Record voice'}</span>
      </button>
      <button
        onClick={() => startRecording('inspire')}
        data-testid="inspire-btn"
        className="flex items-center gap-1 px-2 py-1.5 rounded-lg border border-[#F5C84230] text-[9px] text-[#F5C842] hover:bg-[#F5C84210] transition-all"
        title={es ? 'Tararea o pon una referencia — PULSE crea algo original inspirado' : 'Hum or play a reference — PULSE creates an original inspired track'}
      >
        <Sparkles size={10} />
        <span>{es ? 'Inspirar' : 'Inspire'}</span>
      </button>
    </div>
  );
}
